const TOAST_CONTAINER_ID = 'taskelloToastContainer';
const PENDING_TOASTS_KEY = 'taskelloPendingToasts';
const DEFAULT_AUTO_HIDE_DELAY = 4000;
const CLICK_HIDE_DELAY = 500;
const ENTER_TIMEOUT = 180;
const LEAVE_TIMEOUT = 1800;

const CONTAINER_CLASS = [
    'tw-fixed',
    'tw-right-5',
    'tw-top-5',
    'tw-z-[4000]',
    'tw-flex',
    'tw-w-[min(100vw-2rem,26rem)]',
    'tw-flex-col',
    'tw-gap-3.5',
    'tw-pointer-events-none',
    'max-sm:tw-bottom-4',
    'max-sm:tw-left-4',
    'max-sm:tw-right-4',
    'max-sm:tw-top-auto',
    'max-sm:tw-w-auto'
].join(' ');

const TOAST_BASE_CLASS = [
    'tw-pointer-events-auto',
    'tw-relative',
    'tw-grid',
    'tw-grid-cols-[0.32rem_auto_minmax(0,1fr)]',
    'tw-items-start',
    'tw-gap-3.5',
    'tw-overflow-hidden',
    'tw-rounded-[1.15rem]',
    'tw-border',
    'tw-backdrop-blur-xl',
    'tw-shadow-card',
    'tw-transition-all',
    'tw-duration-200',
    'tw-ease-out',
    'tw-opacity-0',
    'tw-translate-y-[-10px]',
    'tw-scale-[0.98]',
    'max-sm:tw-grid-cols-[0.32rem_auto_minmax(0,1fr)]'
].join(' ');

const TOAST_VISIBLE_CLASS = ['tw-opacity-100', 'tw-translate-y-0', 'tw-scale-100'].join(' ');
const TOAST_HIDDEN_CLASS = ['tw-opacity-0', 'tw-translate-y-[-10px]', 'tw-scale-[0.98]'].join(' ');
const HEADER_CLASS = ['tw-flex', 'tw-items-start', 'tw-justify-between', 'tw-gap-3'].join(' ');
const CONTENT_CLASS = ['tw-min-w-0', 'tw-pr-4', 'tw-py-4'].join(' ');
const MESSAGE_CLASS = ['tw-mt-1', 'tw-text-sm', 'tw-leading-6', 'tw-text-muted', 'tw-break-words'].join(' ');
const TITLE_CLASS = ['tw-block', 'tw-min-w-0', 'tw-text-sm', 'tw-font-semibold', 'tw-leading-5', 'tw-text-text'].join(' ');
const CLOSE_CLASS = [
    'tw-inline-flex',
    'tw-h-8',
    'tw-w-8',
    'tw-flex-shrink-0',
    'tw-items-center',
    'tw-justify-center',
    'tw-rounded-xl',
    'tw-text-muted',
    'tw-transition',
    'hover:tw-bg-surface-hover',
    'hover:tw-text-text',
    'focus-visible:tw-outline-none',
    'focus-visible:tw-ring-2',
    'focus-visible:tw-ring-brand/20'
].join(' ');

const TOAST_TYPE_CONFIG = {
    success: {
        icon: 'check-circle-fill',
        title: 'Sukces',
        toastClass: 'tw-border-success/20 tw-bg-surface',
        accentClass: 'tw-bg-success',
        iconClass: 'tw-text-success'
    },
    info: {
        icon: 'info-circle-fill',
        title: 'Informacja',
        toastClass: 'tw-border-info/20 tw-bg-surface',
        accentClass: 'tw-bg-info',
        iconClass: 'tw-text-info'
    },
    warning: {
        icon: 'exclamation-triangle-fill',
        title: 'Uwaga',
        toastClass: 'tw-border-warning/25 tw-bg-surface',
        accentClass: 'tw-bg-warning',
        iconClass: 'tw-text-warning'
    },
    danger: {
        icon: 'x-octagon-fill',
        title: 'Błąd',
        toastClass: 'tw-border-danger/20 tw-bg-surface',
        accentClass: 'tw-bg-danger',
        iconClass: 'tw-text-danger'
    }
};

const normalizeType = (type = 'info') => {
    if (type === 'error') {
        return 'danger';
    }

    return Object.hasOwn(TOAST_TYPE_CONFIG, type) ? type : 'info';
};

const ensureContainer = () => {
    let container = document.getElementById(TOAST_CONTAINER_ID);

    if (container) {
        return container;
    }

    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = CONTAINER_CLASS;
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-relevant', 'additions text');
    document.body.appendChild(container);

    return container;
};

const clearContainerIfEmpty = (container) => {
    if (container && !container.childElementCount) {
        container.remove();
    }
};

const normalizeQueuedToast = (toast) => {
    if (!toast || typeof toast !== 'object' || !toast.message) {
        return null;
    }

    return {
        message: String(toast.message),
        type: normalizeType(toast.type),
        options: toast.options && typeof toast.options === 'object' ? toast.options : {}
    };
};

const readQueuedToasts = () => {
    try {
        const raw = window.sessionStorage.getItem(PENDING_TOASTS_KEY);
        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map(normalizeQueuedToast)
            .filter(Boolean);
    } catch {
        return [];
    }
};

const writeQueuedToasts = (toasts) => {
    try {
        if (!Array.isArray(toasts) || toasts.length === 0) {
            window.sessionStorage.removeItem(PENDING_TOASTS_KEY);
            return;
        }

        window.sessionStorage.setItem(PENDING_TOASTS_KEY, JSON.stringify(toasts));
    } catch {
    }
};

const createButton = (className, label, icon) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    button.innerHTML = `<i class="bi bi-${icon}" aria-hidden="true"></i>`;

    return button;
};

const createToastElement = (message, type, options) => {
    const config = TOAST_TYPE_CONFIG[type];
    const toast = document.createElement('section');
    const headerText = typeof options.header === 'string' && options.header.trim() !== ''
        ? options.header.trim()
        : '';
    const isClosable = options.closable !== false;
    const hasHeader = headerText !== '';

    toast.className = `${TOAST_BASE_CLASS} ${config.toastClass}`;
    toast.setAttribute('role', type === 'danger' ? 'alert' : 'status');
    toast.setAttribute('aria-atomic', 'true');

    const accent = document.createElement('div');
    accent.className = `${config.accentClass} tw-h-full`;
    toast.appendChild(accent);

    const iconWrap = document.createElement('div');
    iconWrap.className = [
        hasHeader ? 'tw-mt-2' : 'tw-mt-1.5',
        'tw-inline-flex',
        'tw-h-10',
        'tw-w-10',
        'tw-flex-shrink-0',
        'tw-items-center',
        'tw-justify-center',
        'tw-text-base',
        config.iconClass
    ].join(' ');
    iconWrap.innerHTML = `<i class="bi bi-${config.icon}" aria-hidden="true"></i>`;
    toast.appendChild(iconWrap);

    const content = document.createElement('div');
    content.className = hasHeader ? CONTENT_CLASS : 'tw-min-w-0 tw-pr-4 tw-py-3';

    if (hasHeader) {
        const header = document.createElement('div');
        header.className = HEADER_CLASS;

        const title = document.createElement('strong');
        title.className = TITLE_CLASS;
        title.textContent = headerText;
        header.appendChild(title);

        if (isClosable) {
            const closeButton = createButton(CLOSE_CLASS, 'Zamknij powiadomienie', 'x-lg');
            closeButton.dataset.toastDismiss = 'true';
            header.appendChild(closeButton);
        }

        content.appendChild(header);
    }

    const body = document.createElement('div');
    body.className = hasHeader
        ? MESSAGE_CLASS
        : 'tw-flex tw-items-start tw-justify-between tw-gap-3 tw-text-sm tw-leading-6 tw-text-muted tw-break-words';
    body.textContent = String(message);

    if (!hasHeader && isClosable) {
        body.textContent = '';

        const messageElement = document.createElement('div');
        messageElement.className = 'tw-min-w-0 tw-flex-1 tw-break-words';
        messageElement.textContent = String(message);
        body.appendChild(messageElement);

        const closeButton = createButton(`${CLOSE_CLASS} tw--mr-1 tw--mt-1`, 'Zamknij powiadomienie', 'x-lg');
        closeButton.dataset.toastDismiss = 'true';
        body.appendChild(closeButton);
    } else {
        body.textContent = String(message);
    }

    content.appendChild(body);

    toast.appendChild(content);

    return toast;
};

const show = (message, type = 'info', options = {}) => {
    window.Taskello?.debugLog?.('showToast', message, type, options);

    const container = ensureContainer();
    const normalizedType = normalizeType(type);
    const toast = createToastElement(message, normalizedType, options);
    const autoHide = options.autoHide !== false;
    const autoHideDelay = Number.isInteger(options.autoHideDelay) ? options.autoHideDelay : DEFAULT_AUTO_HIDE_DELAY;
    const onClose = typeof options.onClose === 'function' ? options.onClose : null;

    let removed = false;
    let removeTimer;
    let clickHideTimer;

    const removeToast = () => {
        if (removed) {
            return;
        }

        removed = true;
        window.clearTimeout(removeTimer);
        window.clearTimeout(clickHideTimer);
        toast.classList.remove(...TOAST_VISIBLE_CLASS.split(' '));
        toast.classList.add(...TOAST_HIDDEN_CLASS.split(' '));

        window.setTimeout(() => {
            toast.remove();
            clearContainerIfEmpty(container);

            if (onClose) {
                onClose();
            }
        }, 180);
    };

    if (autoHide) {
        removeTimer = window.setTimeout(removeToast, autoHideDelay);
    }

    toast.addEventListener('mouseenter', () => {
        window.clearTimeout(removeTimer);
    });

    toast.addEventListener('mouseleave', () => {
        if (!autoHide || removed) {
            return;
        }

        removeTimer = window.setTimeout(removeToast, LEAVE_TIMEOUT);
    });

    toast.addEventListener('click', (event) => {
        if (event.target.closest('[data-toast-dismiss="true"]')) {
            removeToast();
            return;
        }

        if (!autoHide) {
            return;
        }

        window.clearTimeout(clickHideTimer);
        clickHideTimer = window.setTimeout(removeToast, CLICK_HIDE_DELAY);
    });

    container.appendChild(toast);

    requestAnimationFrame(() => {
        toast.classList.remove(...TOAST_HIDDEN_CLASS.split(' '));
        toast.classList.add(...TOAST_VISIBLE_CLASS.split(' '));
    });

    return {
        element: toast,
        hide: removeToast
    };
};

const queueToast = (message, type = 'info', options = {}) => {
    const queuedToasts = readQueuedToasts();
    queuedToasts.push({
        message: String(message),
        type: normalizeType(type),
        options
    });
    writeQueuedToasts(queuedToasts);
};

const flushQueuedToasts = () => {
    const queuedToasts = readQueuedToasts();
    if (!queuedToasts.length) {
        return;
    }

    writeQueuedToasts([]);
    queuedToasts.forEach((toast) => {
        show(toast.message, toast.type, toast.options);
    });
};

window.Taskello = window.Taskello || {};
window.Taskello.toast = {
    show,
    queue: queueToast,
    flush: flushQueuedToasts
};
window.Taskello.showToast = show;

window.taskello = window.taskello || {};
window.taskello.showToast = show;
window.taskello.queueToast = queueToast;
window.taskello.flushQueuedToasts = flushQueuedToasts;

window.showToast = show;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flushQueuedToasts, { once: true });
} else {
    flushQueuedToasts();
}
